// « Inviter » (D-075) : une joueuse présente une amie (son coach valide) ; tout compte du club
// invite un bénévole, dans le groupe de son choix : une mission Ambassade (D-100) quand la recrue tient son
// premier créneau. Le coach y retrouve « Inviter l'équipe ». Un compte mineur n'invite pas de bénévole (D-103 :
// refusé aussi par la base) ; il peut présenter une amie à son coach. Un compte partenaire (aussi bénévole) : au vouvoiement (D-103).
import { clubVolunteerGroups, myInvitations, roleInviteLink, teamInviteLink, type MyInvitation, type VolunteerGroup } from '../lib/api';
import { firstName, isMinorAccount, me, modOn, type Ctx } from './context';
import { agree } from '../domain/labels';
import { $, html, mount, openSheet, toast, type Html } from './dom';
import { inviteUrl, openShare, since } from './share';
import { tv } from './vous';

const STATUS: Record<MyInvitation['status'], [string, string]> = {
  sent: ['pas encore ouvert', 'chip-mute'], opened: ['lien ouvert', 'chip-mute'], joined: ['pas encore de créneau', 'chip-mute'],
  credited: ['✓ mission Ambassade', ''], expired: ['lien expiré', 'chip-mute'], revoked: ['lien désactivé', 'chip-mute'],
  pending: ['chez le coach', 'chip-warn'], trial: ['à l’essai', 'chip-mute'], declined: ['pas retenu', 'chip-mute'],
};

export async function renderInviteMe(root: HTMLElement, ctx: Ctx): Promise<void> {
  const club = ctx.club;
  if (!club) { mount(root, html`<div class="empty"><p class="empty-title">Aucun club.</p></div>`); return; }
  const self = me(ctx);
  // Les équipes où je joue moi-même (pas celles de mes enfants), et celles que j'encadre.
  const playIn = ctx.full.teams.filter(t => self && ctx.full.memberships.some(m => m.team_id === t.id && m.person_id === self.id));
  // « Inviter les U13 filles » : l'outil du coach, dans sa casquette ; pas dans la casquette Bénévole (D-097).
  const coachOf = ctx.hat === 'benevole' ? [] : ctx.full.teams.filter(t => ctx.full.pureCoach.has(t.id));
  const volunteers = !isMinorAccount(ctx) && modOn(ctx, 'benevolat');
  const [mine, groups] = await Promise.all([
    myInvitations(club.id).catch(() => [] as MyInvitation[]),
    clubVolunteerGroups(club.id).catch(() => [] as VolunteerGroup[]),
  ]);
  const reload = () => void renderInviteMe(root, ctx);
  const t = (tu: string, vous: string) => tv(ctx, tu, vous);
  const row = (i: MyInvitation): Html => html`
    <li class="iv-row"><span class="iv-main"><strong>${i.name}</strong><span class="muted small">${i.kind === 'friend' ? `Veut jouer · ${i.detail}` : `Bénévole · ${i.detail}`} · ${since(i.at)}</span></span>
      ${i.kind === 'friend' && i.status === 'joined' ? html`<span class="chip">✓ dans l’effectif</span>` : html`<span class="chip ${STATUS[i.status][1]}">${STATUS[i.status][0]}</span>`}</li>`;

  mount(root, html`
    <section class="screen">
      <header class="screen-head"><p class="eyebrow">${firstName(ctx) || 'Moi'}${playIn[0] ? ` · ${playIn[0].name}` : ''}</p><h2>Inviter</h2></header>
      ${coachOf.length ? html`
        <section class="block">
          <h3>Ton équipe</h3>
          ${coachOf.map(t => html`<a class="card card-row" href="#/inviter/equipe/${t.id}">
            <span class="play-thumb" aria-hidden="true"><span class="ti ti-people"></span></span>
            <span class="card-main"><span class="card-title">Inviter les ${t.name}</span><span class="muted small">Un lien par personne, relié à sa fiche ; les demandes à traiter.</span></span>
            <span class="occ-go" aria-hidden="true">›</span></a>`)}
        </section>` : ''}
      ${playIn.length ? html`
        <section class="card iv-card">
          <h3>🏀 Quelqu’un qui veut jouer</h3>
          <p class="muted small">${agree(playIn[0]!.gender, 'Ton ami se présente', 'Ton amie se présente')} avec ton lien ; ton coach valide (essai d’abord, ou directement dans l’effectif).</p>
          ${playIn.length > 1 ? html`<label class="field"><span>Équipe</span><select id="iv-team">${playIn.map(t => html`<option value="${t.id}">${t.name}</option>`)}</select></label>` : ''}
          <button type="button" class="btn btn-primary" id="iv-friend" data-guide="iv-friend">Envoyer mon lien d’invitation</button>
        </section>` : ''}
      ${!coachOf.length && !playIn.length && !volunteers ? html`<div class="empty"><p class="empty-title">Rien à envoyer d’ici pour l’instant.</p>
        <p>${t('Pour faire venir quelqu’un qui veut jouer, parles-en au coach de l’équipe.', 'Pour faire venir quelqu’un qui veut jouer, parlez-en au coach de l’équipe.')}</p></div>` : ''}
      ${!volunteers ? '' : html`<section class="card iv-card">
        <h3>🙌 Quelqu’un qui veut donner un coup de main</h3>
        <p class="muted small">${t('Il entre comme bénévole, dans le groupe de ton choix.', 'Il entre comme bénévole, dans le groupe de votre choix.')}</p>
        <button type="button" class="btn ${playIn.length ? 'btn-ghost' : 'btn-primary'}" id="iv-vol">Inviter un bénévole</button>
      </section>`}
      ${mine.length ? html`
        <section class="block">
          <h3>${t('Mes invitations', 'Vos invitations')}</h3>
          <ul class="iv-list">${mine.map(row)}</ul>
        </section>` : ''}
      ${!volunteers ? '' : html`<p class="hint">${t('Une recrue qui tient son premier créneau = une mission Ambassade dans tes badges.', 'Une recrue qui tient son premier créneau = une mission Ambassade dans vos badges.')}</p>`}
    </section>`);

  $(root, '#iv-friend')?.addEventListener('click', async () => {
    const teamId = ($<HTMLSelectElement>(root, '#iv-team')?.value) ?? playIn[0]!.id;
    const team = playIn.find(t => t.id === teamId)!;
    try {
      const l = await teamInviteLink(teamId);
      openShare('Mon lien d’invitation', 'À envoyer à qui veut jouer avec ton équipe. La personne se présente, ton coach valide.',
        `Viens jouer avec nous chez les ${team.name} (${club.name}) ! Présente-toi ici, le coach te répond : ${inviteUrl(l.token)}`);
    } catch { toast('Lien impossible pour le moment.', 'ko'); }
  });

  $(root, '#iv-vol')?.addEventListener('click', () => {
    openSheet('Inviter un bénévole', html`
      <form class="form" id="f-vol" novalidate>
        <label class="field"><span>Son prénom</span><input name="name" maxlength="60" required placeholder="ex. Josette"></label>
        ${groups.length ? html`<label class="field"><span>Dans le groupe</span><select name="group">
          <option value="">Pas de groupe (il choisira)</option>
          ${groups.map(g => html`<option value="${g.id}">${g.emoji} ${g.name}${g.skill ? ` · ${g.skill}` : ''}</option>`)}</select></label>` : ''}
        <p class="form-msg" role="alert"></p>
        <button class="btn btn-primary" type="submit">Créer son lien</button>
        <p class="hint">${t('Un lien pour une personne, valable 7 jours. Tu l’envoies par WhatsApp ou SMS.', 'Un lien pour une personne, valable 7 jours. Vous l’envoyez par WhatsApp ou SMS.')}</p>
      </form>`, (sheet, close) => {
      const f = $<HTMLFormElement>(sheet, '#f-vol')!;
      f.addEventListener('submit', async e => {
        e.preventDefault();
        const d = new FormData(f);
        const name = String(d.get('name') ?? '').trim();
        const group = String(d.get('group') ?? '') || null;
        if (!name) { $(f, '.form-msg')!.textContent = 'Son prénom, pour le lien.'; return; }
        try {
          const l = await roleInviteLink(club.id, 'supporter', { name, group });
          const g = groups.find(x => x.id === group);
          close();
          openShare(`Le lien de ${name}`, `${name} donne son e-mail, reçoit un code, et entre comme bénévole${g ? ` chez ${g.name}` : ''}.`,
            t(`${name}, merci de nous donner un coup de main au ${club.name} ! Ton accès à l’app des bénévoles${g ? ` (${g.name})` : ''} : ${inviteUrl(l.token)} — tu donnes ton e-mail, tu reçois un code, c’est tout.`,
              `${name}, merci de donner un coup de main au ${club.name} ! Votre accès à l’app des bénévoles${g ? ` (${g.name})` : ''} : ${inviteUrl(l.token)} — vous donnez votre e-mail, vous recevez un code, c’est tout.`));
          reload();
        } catch (err) {
          $(f, '.form-msg')!.textContent = String((err as Error).message).includes('trop') ? t('Beaucoup de liens aujourd’hui : réessaie demain.', 'Beaucoup de liens aujourd’hui : réessayez demain.') : 'Lien impossible pour le moment.';
        }
      });
    });
  });
}
